import React from 'react';
import PageLayout from '../../components/layout/PageLayout';
import Section from '../../components/layout/Section';
import HeroSection from '../../components/common/HeroSection';
import CTASection from '../../components/common/CTASection';
import ServiceGalaxy from './components/ServiceGalaxy';
import ServiceCard from './components/ServiceCard';
import ProcessVisualization from './components/ProcessVisualization';
import ServiceSelector from './components/ServiceSelector';

const ServicesOverview = () => {
  const services = [
    {
      id: 1,
      title: 'Traditional Infrastructure Services',
      subtitle: 'Foundation Infrastructure Solutions',
      description: 'Comprehensive infrastructure services including tower erection, site surveys, electrical services, and construction. Our traditional infrastructure expertise forms the backbone of reliable telecommunications networks.',
      image: 'https://images.unsplash.com/photo-1560472354-b33ff0c44a43?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      icon: 'Building',
      iconBg: 'bg-blue-100',
      iconColor: 'text-blue-600',
      category: 'Infrastructure',
      categoryColor: 'bg-blue-600',
      features: [
        'Tower Erection and Strengthening',
        'Site Surveys (SBC, SST, NDT, Core Cutting)',
        'Electrical Services (EB Connections)',
        'Boundary Walls and Staff Rooms',
        'Civil Construction',
        'Infrastructure Planning'
      ],
      technologies: ['Construction Equipment', 'Survey Tools', 'Electrical Systems', 'Safety Equipment', 'Quality Testing', 'Project Management'],
      testimonial: {
        quote: 'ONS METTLE delivered robust infrastructure solutions that exceeded our durability and performance requirements.',
        author: 'Rajesh Kumar',
        position: 'Project Director, Infrastructure Corp'
      },
      stats: {
        projects: '50+',
        success: '98%'
      },
      roi: '280%',
      roiPercentage: '70%'
    },
    {
      id: 2,
      title: 'Innovation Solutions',
      subtitle: 'Cutting-Edge Technology Solutions',
      description: 'Advanced innovation solutions including custom tower design to solve COLO congestion issues and laser technology for EMF and Broadband surveys. We pioneer tomorrow\'s telecommunications infrastructure.',
      image: 'https://images.pexels.com/photos/3184291/pexels-photo-3184291.jpeg?auto=compress&cs=tinysrgb&w=1000&q=80',
      icon: 'Lightbulb',
      iconBg: 'bg-orange-100',
      iconColor: 'text-orange-600',
      category: 'Innovation',
      categoryColor: 'bg-orange-600',
      features: [
        'Custom Tower Design Solutions',
        'COLO Congestion Problem Solving',
        'Laser Technology for EMF Surveys',
        'Broadband Survey Technology',
        'Advanced Engineering Design',
        'Technology Integration'
      ],
      technologies: ['Laser Technology', 'EMF Measurement', 'Custom Design Software', 'Advanced Modeling', 'Innovation Platforms', 'R&D Tools'],
      testimonial: {
        quote: 'Their innovative approach solved our complex COLO congestion issues with custom tower designs that exceeded expectations.',
        author: 'Priya Sharma',
        position: 'Technical Director, TelecomTech'
      },
      stats: {
        projects: '30+',
        success: '100%'
      },
      roi: '350%',
      roiPercentage: '88%'
    },
    {
      id: 3,
      title: 'Telecom Services',
      subtitle: 'Advanced Telecommunications Solutions',
      description: 'Comprehensive telecom services including advanced surveys, engineering design for cost-effective deployment, and specialized telecommunications infrastructure solutions.',
      image: 'https://images.pixabay.com/photo/2017/05/10/13/40/art-2301646_1280.jpg?auto=compress&cs=tinysrgb&w=1000&q=80',
      icon: 'Radio',
      iconBg: 'bg-purple-100',
      iconColor: 'text-purple-600',
      category: 'Telecommunications',
      categoryColor: 'bg-purple-600',
      features: [
        'Advanced Surveys',
        'Engineering Design for Cost-Effective Deployment',
        'Network Architecture Planning',
        'Signal Optimization',
        'Telecommunications Infrastructure',
        'Performance Analysis'
      ],
      technologies: ['5G/LTE', 'Fiber Optics', 'RF Systems', 'Network Protocols', 'Survey Equipment', 'Design Software'],
      testimonial: {
        quote: 'Their telecom expertise helped us achieve cost-effective deployment with superior network performance.',
        author: 'Amit Patel',
        position: 'Network Manager, ConnectSolutions'
      },
      stats: {
        projects: '75+',
        success: '97%'
      },
      roi: '290%',
      roiPercentage: '73%'
    },
    {
      id: 4,
      title: 'Turnkey Capabilities',
      subtitle: 'Complete End-to-End Solutions',
      description: 'Comprehensive turnkey capabilities including TSP, Civil, Electrical, Mechanical services, Project and Construction Management, Soil testing, and Operations & Maintenance (R&M).',
      image: 'https://images.unsplash.com/photo-1454165804606-c3d57bc86b40?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1000&q=80',
      icon: 'Settings',
      iconBg: 'bg-green-100',
      iconColor: 'text-green-600',
      category: 'Turnkey',
      categoryColor: 'bg-green-600',
      features: [
        'TSP (Telecom Service Provider) Services',
        'Civil Engineering Solutions',
        'Electrical System Implementation',
        'Mechanical Services',
        'Project and Construction Management',
        'Soil Testing',
        'Operations & Maintenance (R&M)'
      ],
      technologies: ['Project Management Tools', 'Construction Equipment', 'Testing Instruments', 'Maintenance Systems', 'Quality Control', 'Safety Systems'],
      testimonial: {
        quote: 'Their turnkey approach provided us with a seamless end-to-end solution that exceeded all our project requirements.',
        author: 'Sunita Verma',
        position: 'Operations Director, InfraTech Solutions'
      },
      stats: {
        projects: '40+',
        success: '99%'
      },
      roi: '320%',
      roiPercentage: '80%'
    }
  ];

  return (
    <PageLayout
      title="Services Overview - Comprehensive Telecom Solutions"
      description="From traditional infrastructure to cutting-edge innovation solutions, we provide comprehensive end-to-end services across telecommunications, engineering, and turnkey capabilities."
      keywords={['telecom services', 'infrastructure', 'innovation solutions', 'turnkey capabilities']}
      backgroundClass="bg-gradient-to-b from-slate-900 via-slate-800 to-slate-900"
      includeHelmet={true}
    >
      <HeroSection
        title={
          <>
            Comprehensive Telecom
            <span className="holographic"> Solutions</span>
          </>
        }
        description="From traditional infrastructure to cutting-edge innovation solutions, we provide comprehensive end-to-end services across telecommunications, engineering, and turnkey capabilities. Each service is backed by over 10 years of collaborative expertise."
        primaryCTA={{
          text: "Explore Our Solutions",
          onClick: () => console.log("Explore clicked")
        }}
        secondaryCTA={{
          text: "Schedule Consultation",
          onClick: () => console.log("Schedule clicked")
        }}
        backgroundClass="bg-gradient-to-br from-slate-900 via-slate-800 to-blue-900"
      />

      {/* Service Galaxy */}
      <ServiceGalaxy />

      {/* Individual Service Cards */}
      <Section
        background="bg-gradient-to-b from-slate-800 to-slate-900 relative overflow-hidden"
        padding="py-20"
        animate={true}
      >
        {/* Background Effects */}
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-40 left-20 w-96 h-96 bg-brand-green/20 rounded-full blur-3xl animate-pulse" />
          <div className="absolute bottom-40 right-20 w-80 h-80 bg-brand-orange/20 rounded-full blur-3xl animate-pulse" />
        </div>

        <div className="relative z-10">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6 font-heading">
              Our Service
              <span className="holographic"> Categories</span>
            </h2>
            <p className="text-xl text-white max-w-3xl mx-auto font-body">
              Four comprehensive service categories covering traditional infrastructure, innovation solutions, telecommunications, and turnkey capabilities - all designed to connect clients to tomorrow's technology today.
            </p>
          </div>

          <div className="space-y-16">
            {services.map((service, index) => (
              <ServiceCard key={service.id} service={service} index={index} />
            ))}
          </div>
        </div>
      </Section>

      {/* Process Visualization */}
      <ProcessVisualization />

      {/* Service Selector */}
      <ServiceSelector />

      {/* Final CTA Section */}
      <CTASection
        title="Ready to Transform Your Business?"
        description="Let's discuss how our expertise can accelerate your success. Schedule a consultation to explore the possibilities."
        primaryCTA={{
          text: "Schedule Free Consultation",
          onClick: () => console.log("Schedule consultation clicked")
        }}
        secondaryCTA={{
          text: "Download Service Guide",
          onClick: () => console.log("Download guide clicked")
        }}
        disclaimer="No commitment required • 30-minute strategic discussion • Immediate insights"
        backgroundClass="bg-gradient-to-r from-gray-900 to-blue-900"
      />
    </PageLayout>
  );
};

export default ServicesOverview;